import { msg } from "@lingui/core/macro";
import { KeyedMutator } from "swr";
import { OrderStatus, PurchaseOrderStatus } from "./orderStatusMap";
import { OrderRegion } from "../../types/regions";
import { MDXRichText } from "..";

export interface WootingCustomerName {
  first_name: string;
  last_name: string;
}
export interface WootingUser {
  customer_name: WootingCustomerName | null;
  hasLinkedDiscord: boolean;
  name_on_website?: string;
}

export interface DiscordUserResponse {
  id: string;
  username: string;
  avatar: string;
  displayAvatarURL: string;
  discriminator: string;
  public_flags: number;
  flags: number;
  banner: string;
  banner_color: string;
  accent_color: number;
  locale: string;
  mfa_enabled: boolean;
  premium_type: number;
  tag: string;
  rewarded: RoleReward[];
  notifications: string[];
}

export interface RoleReward {
  roleId: string;
  rewardName: string;
}

export type UserMutate = KeyedMutator<DiscordUserResponse | null>;

export type OrderTracking = {
  shipment_method: string;
  tracking_url: string;
};

export enum OrderDeliveryStage {
  New = 0,
  PreProduction,
  MassProduction,
  WarehouseShipment,
  InWarehouse,
  ConfirmedWarehouse,
  LastMile, // Status Page Only
  Unknown,
  Cancelled, // Hub Only
  RMA, // Hub Only
  Enroute, // Hub Only
  /**
   * @deprecated Use `OrderDeliveryStage.Enroute` instead
   */
  Delivered, // Hub Only
  Replacements,
}

interface EstimatedDeliveryBase {
  approximate: boolean;
  batchName: string | undefined;
  forSku?: string;
}

export interface EstimatedDeliveryApprox extends EstimatedDeliveryBase {
  approximate: true;
  date: string; // serialized Date
}

export interface EstimatedDeliveryExact extends EstimatedDeliveryBase {
  approximate: false;
  dateStart: string; // serialized Date
  dateEnd: string; // serialized Date
  isPeakVolume: boolean;
}

export type EstimatedDelivery =
  EstimatedDeliveryApprox | EstimatedDeliveryExact;

export enum NoAddressChangeReason {
  OrderIsShipped = "order_is_shipped",
  OwLabSweepstake = "owlab_sweepstake",
}

export const NoAddressChangeReasonMap = {
  [NoAddressChangeReason.OrderIsShipped]: msg({
    id: "Shipment_address_change_order_shipped",
    message:
      "It is not possible to change your address. The order has already been processed for fulfilment.",
  }),
  [NoAddressChangeReason.OwLabSweepstake]: msg({
    id: "Shipment_address_change_owlab_sweepstake",
    message:
      "This order is part of the Owlab sweepstake. To prevent scalping we do not allow address changes.",
  }),
} as const;

export type ChangeAddress =
  | {
      canChangeAddress: true;
    }
  | {
      canChangeAddress: false;
      reason: NoAddressChangeReason;
    };
export interface OrderDeliveryInfo {
  orderStage: OrderDeliveryStage;
  estimatedDelivery: EstimatedDelivery | undefined;
  canConfirmAddress?: boolean;
  changeAddress: ChangeAddress;
}

export enum HoldReleaseStatus {
  CanHold = "canHold",
  IsHeldCanRelease = "isHeldCanRelease",
  IsHeldCannotRelease = "isHeldCannotRelease",
}

interface HoldDataCanHold {
  hold_status: HoldReleaseStatus.CanHold;
}
interface HoldDataCanRelease {
  hold_status:
    HoldReleaseStatus.IsHeldCannotRelease | HoldReleaseStatus.IsHeldCanRelease;
  hold_code: HoldCode;
  hold_reason?: string;
}
export type OrderHoldData = HoldDataCanHold | HoldDataCanRelease;

export type ChineseAddress = {
  cn_firstName: string;
  cn_lastName: string;
  cn_company?: string | undefined;
  cn_address1: string;
  cn_address2?: string | undefined;
  cn_city: string;
};

export interface OrderCustomsId {
  required: boolean;
  value: string;
  displayName: string;
}

export interface Order {
  id: string;
  source_id: string;
  vat_id?: string;
  email: string;
  shipping_method?: string;
  status: OrderStatus;
  region: OrderRegion;
  date_added: string;
  order_items: OrderItemsEntity[];
  shipping?: Shipping;
  customer?: Shipping;
  order_total: number;
  order_tracking?: OrderTracking[];
  merged_orders?: Order[];
  delivery_info: OrderDeliveryInfo;
  hold_data?: OrderHoldData;
  customs_id?: OrderCustomsId;
  chinese_address?: ChineseAddress;
  transfer_status?: boolean;
  returnStatus?: CanReturnStatus;
}

export type CanReturnStatus =
  "CAN_RETURN" | "RETURNED_24_HOURS" | "PAST_30_DAYS" | "NOT_FULFILLED";

export enum HoldCode {
  RequestedCancel = "requested_cancel",
  ReviewNeeded = "review_needed",
  RefundNeeded = "refund_needed",
  CancelPending = "cancel_pending",
  AddressUpdateRequired = "address_update_required",
  AddressUpdated = "address_updated",
  ShipByDate = "ship_on_date",
  PaymentVerification = "verify_payment_method",
  OrderModification = "order_modification",
  NoShipping = "no_available_shipping_method",
  PendingDispute = "disputed_preorder",
  CustomerHold = "customer_hold",
  WootingHold = "wooting_hold",
  Waiting = "waiting_for",
  PendingMerge = "pending_merge",
  /**
   * @deprecated use `HoldCode.OrderModification` instead
   */
  AwaitingPayment = "payment_required",
  Owlab = "owlab",
  V2ResellerPending = "v2-reseller-pending",
}

export interface OrderItemsEntity {
  sku: string;
  name: string;
  price: number;
  quantity: number;
  image_source: string;
  variant?: string;
  poId?: string;
  batchId?: number;
  batchName?: string;
}

export interface Shipping {
  first_name: string;
  last_name: string;
  company: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone: string;
}

export enum UpdateContentState {
  DONT_SHOW = "DONT_SHOW",
  SHOW_WITHOUT_POST = "SHOW_WITHOUT_POST",
  SHOW_WITH_POST_DRAFT = "SHOW_WITH_POST_DRAFT",
  SHOW_WITH_POST_PUBLISHED = "SHOW_WITH_POST_PUBLISHED",
}

export interface BatchUpdateResponse {
  purchaseOrderId: string;
  purchaseOrderStatus: PurchaseOrderStatus;
  estimatedDelivery: string; // ISO date string
  updateContent: string | null | undefined; // NOTE: undefined for safty
  updateContentState: UpdateContentState;
  createdAt: string;
  updatedAt: string;
  purchaseOrderDisplayName: string;
  batchUpdate: {
    data: {
      id: string;
      author: { firstName: string; avatar: string };
      published: boolean;
      title: string;
      tldr: MDXRichText;
      fullDescription: MDXRichText;
      createdAt: string;
      updatedAt: string;
    };
  };
}
